import { ml } from '../lib/profiles';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PenLine, Eraser, Check, X, Trash2 } from 'lucide-react';

// crop to the strokes, downscale (max 600x220) and flatten on white -> small JPEG
export function sigImage(c) {
  const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
  let x0 = c.width, y0 = c.height, x1 = 0, y1 = 0;
  for (let y = 0; y < c.height; y += 2) for (let x = 0; x < c.width; x += 2) if (d[(y * c.width + x) * 4 + 3] > 20) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  if (x1 <= x0) return c.toDataURL('image/jpeg', 0.85);
  const pad = 12; x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(c.width, x1 + pad); y1 = Math.min(c.height, y1 + pad);
  const w = x1 - x0, h = y1 - y0, s = Math.min(1, 600 / w, 220 / h), o = document.createElement('canvas');
  o.width = Math.round(w * s); o.height = Math.round(h * s);
  const cx = o.getContext('2d'); cx.fillStyle = '#fff'; cx.fillRect(0, 0, o.width, o.height); cx.drawImage(c, x0, y0, w, h, 0, 0, o.width, o.height);
  return o.toDataURL('image/jpeg', 0.85);
}

// full-screen finger/stylus signature pad -> PNG data URL
export function Pad({ t, onDone, onClose }) {
  const ref = useRef(null);
  const draw = useRef(null);
  const [empty, setEmpty] = useState(true);
  useEffect(() => {
    const c = ref.current, r = c.getBoundingClientRect(), dpr = window.devicePixelRatio || 1;
    c.width = r.width * dpr; c.height = r.height * dpr;
    const cx = c.getContext('2d'); cx.scale(dpr, dpr); cx.lineWidth = 2.6; cx.lineCap = 'round'; cx.lineJoin = 'round'; cx.strokeStyle = '#0b1f4d';
  }, []);
  const pt = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.currentTarget.setPointerCapture?.(e.pointerId); draw.current = pt(e); };
  const move = (e) => {
    if (!draw.current) return;
    const cx = ref.current.getContext('2d'), p = pt(e);
    cx.beginPath(); cx.moveTo(...draw.current); cx.lineTo(...p); cx.stroke();
    draw.current = p; setEmpty(false);
  };
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setEmpty(true); };
  return createPortal(
    <div className="sig-ov" data-testid="signature-pad">
      <div className="sig-head"><b><PenLine size={16} /> {t.sigPadHint}</b><button className="icon-btn" data-testid="signature-pad-close" onClick={onClose}><X size={18} /></button></div>
      <canvas ref={ref} className="sig-canvas" data-testid="signature-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={() => { draw.current = null; }} onPointerCancel={() => { draw.current = null; }} />
      <div className="sig-foot">
        <button className="btn btn-outline" data-testid="signature-clear-btn" onClick={clear}><Eraser size={16} /> {t.sigClear}</button>
        <button className="btn btn-green" data-testid="signature-confirm-btn" disabled={empty} onClick={() => onDone(sigImage(ref.current))}><Check size={16} /> {t.sigConfirm}</button>
      </div>
    </div>,
    document.body,
  );
}

// "client / supervisor signature" box in the export area (stored in the project)
export default function SignatureBox({ t, project, updateProject }) {
  const s = project.signature || {};
  const [pad, setPad] = useState(false);
  const set = (p) => updateProject((pr) => ({ ...pr, signature: { ...(pr.signature || {}), ...p } }));
  return (
    <div className="sig-box" data-testid="signature-box">
      <b>✍️ {ml(t, 'sigTitle')}</b>
      <div className="row">
        <input data-testid="signature-name-input" placeholder={t.sigName} value={s.name || ''} onChange={(e) => set({ name: e.target.value })} />
        <input data-testid="signature-role-input" placeholder={t.sigRole} value={s.role || ''} onChange={(e) => set({ role: e.target.value })} />
      </div>
      {s.data ? (
        <div className="row">
          <img src={s.data} alt="" className="sig-img" data-testid="signature-image" />
          <small data-testid="signature-date">{new Date(s.at).toLocaleString()}</small>
          <button className="icon-btn danger" data-testid="signature-delete-btn" onClick={() => set({ data: null, at: null })}><Trash2 size={15} /></button>
        </div>
      ) : <button className="btn btn-dark btn-sm" data-testid="signature-sign-btn" onClick={() => setPad(true)}><PenLine size={15} /> {t.sigSign}</button>}
      <small className="muted">{t.sigPdfHint}</small>
      {pad && <Pad t={t} onClose={() => setPad(false)} onDone={(data) => { set({ data, at: Date.now() }); setPad(false); }} />}
    </div>
  );
}
